import type { EnumUnion } from '@dresscode-frontend/utility-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiRequestBody, ApiResponseBody } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { handleApiErrorWithReauth } from '@/lib/api/reauth';
import { queryClient } from '@/lib/queryClient';
export type MfaSettingsRes = ApiResponseBody<'/v2/settings/mfa', 'get'>;
export type MfaSettingsReq = ApiRequestBody<'/v2/settings/mfa/email', 'patch'>;
export const MfaRequiredLevel = {
  NOT_REQUIRED: 'NOT_REQUIRED',
  ANY_FACTOR: 'ANY_FACTOR'
} as const satisfies Record<string, MfaSettingsRes['operatorPolicy']['level']>;
export type MfaRequiredLevel = EnumUnion<typeof MfaRequiredLevel>;
export const mfaSettingsQueryKey = {
  all: ['mfa-settings'] as const
} as const;

// 取得失敗は画面内にエラーを出すため、グローバルエラーダイアログは抑止する。
const LOCAL_ERROR_META = {
  disableGlobalErrorHandling: true
} as const;
export const getMfaSettings = async () => {
  const res = await apiClient.GET('/v2/settings/mfa');
  return getDataOrThrowApiError(res);
};
export const mfaSettingsQueryOptions = () => ({
  queryKey: mfaSettingsQueryKey.all,
  queryFn: () => getMfaSettings(),
  meta: LOCAL_ERROR_META
});

// @SecureApi(PATCH /v2/settings/mfa/email): AAL2 未充足時は 401+code が返るため、
// handleApiErrorWithReauth で再認証→元操作の再実行をラップする。
export const updateMfa = async (requestData: MfaSettingsReq) => handleApiErrorWithReauth(async () => {
  const res = await apiClient.PATCH('/v2/settings/mfa/email', {
    body: requestData
  });
  return getDataOrThrowApiError(res);
});
export const useMfaSettings = () => {
  return useQuery(mfaSettingsQueryOptions());
};
export const useUpdateMfa = () => {
  const {
    mutateAsync,
    isPending
  } = useMutation({
    mutationFn: (requestData: MfaSettingsReq) => {
      return updateMfa(requestData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mfaSettingsQueryKey.all
      });
    }
  });
  return {
    updateMfa: mutateAsync,
    isPending
  };
};